/**
=========================================================
* Material Dashboard 3 PRO React - Custom Sidenav
* Supports Mini + Full Logos with same theme design
=========================================================
*/

import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

// @mui components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import { IconButton, Tooltip } from "@mui/material";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

// ✅ Logos
import fullLogo from "images/carepro.jpg";      // Full logo (expanded)
import miniLogo from "images/carepro.jpg";                // Mini logo (collapsed)
import FooterInfo from "track/FooterInfo";


function Sidenav({ color, brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;

  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) textColor = "dark";
  else if (whiteSidenav && darkMode) textColor = "inherit";

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    function handleResize() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth >= 1200 && transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth >= 1200 && whiteSidenav);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, location]);

  // Renders all routes
  const renderRoutes = routes.map(({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
    if (type === "collapse" || type === "route") {
      if (href)
        return (
          <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} noCollapse={noCollapse} />
          </Link>
        );
      if (noCollapse && route)
        return (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      if (collapse)
        return (
          <SidenavCollapse
            key={key}
            name={name}
            icon={icon}
            active={key === collapseName}
            open={openCollapse === key}
            onClick={() => setOpenCollapse(openCollapse === key ? false : key)}
          >
            {collapse.map(({ name, route, key }) => (
              <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
                <SidenavItem name={name} nested />
              </NavLink>
            ))}
          </SidenavCollapse>
        );
      return (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem color={color} name={name} active={key === itemName} />
        </NavLink>
      );
    }
    if (type === "title")
      return (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={2}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    if (type === "divider")
      return <Divider key={key} light={!darkMode && !whiteSidenav && !transparentSidenav} />;
    return null;
  });

  const isMobile = window.innerWidth < 1200;

return (
  <SidenavRoot
    {...rest}
    variant={isMobile ? "temporary" : "permanent"}
    open={!miniSidenav}
    onClose={() => setMiniSidenav(dispatch, true)}
    ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
  >

      {/* === Header Section === */}
      <MDBox
        pt={2.5}
        pb={0}
        px={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <MDBox
          component={NavLink}
          to="/dashboard"
          display="flex"
          alignItems="center"
          justifyContent={miniSidenav ? "center" : "flex-start"}
          sx={{ textDecoration: "none", width: "100%", transition: "all 0.3s ease-in-out" }}
        >
          {/* ✅ Show mini logo when collapsed, full logo when expanded */}
          <MDBox
            component="img"
            src={miniSidenav ? miniLogo : fullLogo}
            alt="Brand"
            sx={{
              width: miniSidenav ? "2.5rem" : "8rem",
              height: "auto",
              transition: "all 0.3s ease-in-out",
              marginRight: miniSidenav ? 0 : "0.5rem",
              marginLeft: miniSidenav ? 0 : "0.7rem",
            }}
          />
          {!miniSidenav && (
            <MDBox
              width={!brandName && "100%"}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              <MDTypography component="h6" variant="button" fontWeight="regular" color={textColor}>
                {brandName}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>

        {/* Collapse / Expand Icon */}
        <Tooltip title={miniSidenav ? "Expand sidenav" : "Collapse sidenav"} placement="right">
          <IconButton
            onClick={handleMiniSidenav}
            size="small"
            disableRipple
            sx={{
              color: darkMode ? "#fff" : "#344767",
              "&:hover": { backgroundColor: "transparent" },
              transition: "all 0.3s ease-in-out",
            }}
          >
            <Icon>{miniSidenav ? "chevron_right" : "chevron_left"}</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>

      <Divider light={!darkMode && !whiteSidenav && !transparentSidenav} />

{/* Wrap routes and footer together in a vertical layout */}
<MDBox display="flex" flexDirection="column" height="100%">
  {/* All your sidenav routes */}
  <List sx={{ flexGrow: 1 }}>{renderRoutes}</List>

  {/* Footer with user + version info */}
  <FooterInfo />
</MDBox>

    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
};

Sidenav.propTypes = {
  color: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.array.isRequired,
};

export default Sidenav;
